/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { EuiConfirmModal } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { OrganizationOption } from '@modules/organization-api'
import { useSwitchOrganization } from '@modules/organization-lib/hooks/useSwitchOrganization'

interface Props {
  organization: OrganizationOption
  onCancel: () => void
  onlyConfirmWhenLoginEnforced?: boolean
}

const OrganizationSwitchConfirmation = ({
  organization,
  onCancel,
  onlyConfirmWhenLoginEnforced = false,
}: Props) => {
  const { formatMessage } = useIntl()
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false)
  const { switchOrganization, isLoading: isLoadingSwitchOrganization } = useSwitchOrganization()

  return (
    <EuiConfirmModal
      aria-label={formatMessage({
        id: 'organization-switch.confirmation.aria-label',
        defaultMessage: 'Confirm organization switch',
      })}
      title={
        <FormattedMessage
          id='organization-switch.confirmation.title'
          defaultMessage='Switch organization?'
        />
      }
      confirmButtonText={
        <FormattedMessage
          id='organization-switch.confirmation.confirm-button'
          defaultMessage='Switch organization'
        />
      }
      cancelButtonText={
        <FormattedMessage
          id='organization-switch.confirmation.cancel-button'
          defaultMessage='Cancel'
        />
      }
      onCancel={onCancel}
      onConfirm={() =>
        switchOrganization({
          organization,
          onSuccessSwitchingOrganization: () => setIsSwitchingOrganization(true),
        })
      }
      isLoading={isLoadingSwitchOrganization || isSwitchingOrganization}
    >
      {onlyConfirmWhenLoginEnforced ? (
        <FormattedMessage
          id='organization-switch.confirmation.description-reauthentication-required'
          defaultMessage='To switch to {organization}, you need to re-authenticate.'
          values={{
            organization: <strong>{organization.organization_name}</strong>,
          }}
        />
      ) : (
        <FormattedMessage
          id='organization-switch.confirmation.description-switch-organization'
          defaultMessage="You're about to log in to organization {organization}. Switching organizations will discard any unsaved work."
          values={{
            organization: <strong>{organization.organization_name}</strong>,
          }}
        />
      )}
    </EuiConfirmModal>
  )
}

export default OrganizationSwitchConfirmation
